import { FunctionComponent } from 'react'

import config from '@config'
import { enhanceComponentWithErrorBoundary } from '@utils/sentry'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import ABTestManager from '@components/ABTestManager'
import AdManager from '@components/AdManager'
import AnimatedPreviewManager from '@components/AnimatedPreviewManager'
import AureusManager from '@components/AureusManager'
import AuthManager from '@components/AuthManager'
import BlickPlusOnboardingManager from '@components/BlickPlusOnboardingManager'
import ChatbotManager from '@components/Chatbot/ChatbotManager'
import CMPManager from '@components/CMPManager'
import DebugManager from '@components/DebugManager'
import GnsManager from '@components/GoogleNewsShowcase/GNSManager'
import GrowthBookManager from '@components/GrowthBookManager'
import GTMManager from '@components/GTMManager'
import HrefLangLinks from '@components/HrefLangLinks'
import HTMLLinks from '@components/HTMLLinks'
import Kropka from '@components/Kropka'
import ArticleMetaTags from '@components/MetaTags/ArticleMetaTags'
import CommonMetaTags from '@components/MetaTags/CommonMetaTags'
import OneSignalManager from '@components/OneSignalManager'
import Papi from '@components/Papi'
import PianoManager from '@components/Piano/PianoManager'
import PianoMetaTags from '@components/PianoMetaTags'
import PostMessageManager from '@components/PostMessageManager'
import ArticleSchema from '@components/Schema/ArticleSchema'
import AuthorSchema from '@components/Schema/AuthorSchema'
import CueLiveSchema from '@components/Schema/CueLiveSchema'
import NewsMediaOrganizationSchema from '@components/Schema/NewsMediaOrganizationSchema'
import RecipeSchema from '@components/Schema/RecipeSchema'
import SoftwareApplicationSchema from '@components/Schema/SoftwareApplicationSchema'
import VideoSchema from '@components/Schema/VideoSchema'
import WebPageSchema from '@components/Schema/WebPageSchema'
import WebSiteSchema from '@components/Schema/WebSiteSchema'
import SchemaInjector from '@components/SchemaInjector'
import SmartBannerMetaTags from '@components/SmartBannerMetaTags'
import ThemeManager from '@components/ThemeManager'
import Tiun from '@components/Tiun'
import TrackingManager from '@components/TrackingManager'
import TrackPageView from '@components/TrackPageView'
import TrackStartScrolling from '@components/TrackStartScrolling'
import URLParamManager from '@components/URLParamManager'
import VideoManager from '@components/VideoManager'
import ViewportManager from '@components/ViewportManager'

import { CatchAllPageServerProps } from '../../pages/[[...pageUrl]]'

export type NonErrorPagePreCSSComponentsProps = Pick<
  CatchAllPageServerProps,
  'isBlickPlusOfferPage' | 'isUserProfilePage'
> & {
  isStorybook?: boolean
}

const {
  publication: { publication },
} = config

const [
  SchemaInjectorWithErrorBoundary,
  ArticleSchemaWithErrorBoundary,
  RecipeSchemaWithErrorBoundary,
  AuthorSchemaWithErrorBoundary,
  CueLiveSchemaWithErrorBoundary,
  URLParamManagerWithErrorBoundary,
  ABTestManagerWithErrorBoundary,
  AuthManagerWithErrorBoundary,
  PapiWithErrorBoundary,
  PianoManagerWithErrorBoundary,
  AdManagerWithErrorBoundary,
  DebugManagerWithErrorBoundary,
  TrackPageViewWithErrorBoundary,
  TrackStartScrollingWithErrorBoundary,
  KropkaWithErrorBoundary,
  OneSignalManagerWithErrorBoundary,
  CMPManagerWithErrorBoundary,
  ViewportManagerWithErrorBoundary,
  AnimatedPreviewManagerWithErrorBoundary,
  PianoMetaTagsWithErrorBoundary,
  WebSiteSchemaWithErrorBoundary,
  WebPageSchemaWithErrorBoundary,
  SoftwareApplicationSchemaWithErrorBoundary,
  NewsMediaOrganizationSchemaWithErrorBoundary,
  VideoSchemaWithErrorBoundary,
  VideoManagerWithErrorBoundary,
  ChatbotManagerWithErrorBoundary,
  GTMManagerWithErrorBoundary,
  GrowthBookManagerWithErrorBoundary,
  AureusManagerWithErrorBoundary,
  GNSManagerWithErrorBoundary,
  PostMessageManagerWithErrorBoundary,
  ThemeManagerWithErrorBoundary,
  TiunWithErrorBoundary,
  BlickPlusOnboardingManagerWithErrorBoundary,
] = [
  SchemaInjector,
  ArticleSchema,
  RecipeSchema,
  AuthorSchema,
  CueLiveSchema,
  URLParamManager,
  ABTestManager,
  AuthManager,
  Papi,
  PianoManager,
  AdManager,
  DebugManager,
  TrackPageView,
  TrackStartScrolling,
  Kropka,
  OneSignalManager,
  CMPManager,
  ViewportManager,
  AnimatedPreviewManager,
  PianoMetaTags,
  WebSiteSchema,
  WebPageSchema,
  SoftwareApplicationSchema,
  NewsMediaOrganizationSchema,
  VideoSchema,
  VideoManager,
  ChatbotManager,
  GTMManager,
  GrowthBookManager,
  AureusManager,
  GnsManager,
  PostMessageManager,
  ThemeManager,
  Tiun,
  BlickPlusOnboardingManager,
].map((Component) => enhanceComponentWithErrorBoundary(Component))

const NonErrorPagePreCSSComponents: FunctionComponent<
  NonErrorPagePreCSSComponentsProps
> = ({ isBlickPlusOfferPage, isStorybook, isUserProfilePage }) => {
  const { isArticle, isRecipe, isAuthor } = useGetPageIdentity()

  return (
    <>
      {!isStorybook && <HrefLangLinks />}
      {!isStorybook && <CommonMetaTags />}
      {!isStorybook && <ArticleMetaTags />}
      {!isStorybook && <SchemaInjectorWithErrorBoundary />}
      {!isStorybook && <VideoSchemaWithErrorBoundary />}
      {!isStorybook && <NewsMediaOrganizationSchemaWithErrorBoundary />}
      {!isStorybook && <WebSiteSchemaWithErrorBoundary />}
      {!isStorybook && <WebPageSchemaWithErrorBoundary />}
      {!isStorybook && <SoftwareApplicationSchemaWithErrorBoundary />}
      {!isStorybook &&
        isArticle &&
        !isRecipe &&
        !isAuthor &&
        !isBlickPlusOfferPage &&
        !isUserProfilePage && <ArticleSchemaWithErrorBoundary />}
      {!isStorybook && <PianoMetaTagsWithErrorBoundary />}
      {!isStorybook && <SmartBannerMetaTags />}
      {!isStorybook &&
        isRecipe &&
        !isBlickPlusOfferPage &&
        !isUserProfilePage && <RecipeSchemaWithErrorBoundary />}
      {!isStorybook &&
        isAuthor &&
        !isBlickPlusOfferPage &&
        !isUserProfilePage && <AuthorSchemaWithErrorBoundary />}
      {!isStorybook && <CueLiveSchemaWithErrorBoundary />}
      {!isStorybook && <HTMLLinks />}

      {!isStorybook && <ThemeManagerWithErrorBoundary />}
      <PostMessageManagerWithErrorBoundary />
      <ViewportManagerWithErrorBoundary />
      <URLParamManagerWithErrorBoundary />
      <ABTestManagerWithErrorBoundary isStorybook={!!isStorybook} />
      <DebugManagerWithErrorBoundary />
      <PapiWithErrorBoundary />
      <AnimatedPreviewManagerWithErrorBoundary />
      <VideoManagerWithErrorBoundary />
      <TrackingManager />
      <KropkaWithErrorBoundary />

      <ChatbotManagerWithErrorBoundary />

      <AuthManagerWithErrorBoundary />
      {!isStorybook && <GrowthBookManagerWithErrorBoundary />}

      <CMPManagerWithErrorBoundary shouldMockCMP={!!isStorybook} />
      {!isStorybook && <PianoManagerWithErrorBoundary />}
      {!isStorybook && <AdManagerWithErrorBoundary />}
      {!isStorybook && publication !== 'romandie' && <TiunWithErrorBoundary />}

      <TrackPageViewWithErrorBoundary />
      <TrackStartScrollingWithErrorBoundary />
      {!isStorybook && <OneSignalManagerWithErrorBoundary />}
      {!isStorybook && <GTMManagerWithErrorBoundary />}

      {!isStorybook && <AureusManagerWithErrorBoundary />}
      {!isStorybook && <GNSManagerWithErrorBoundary />}
      {!isStorybook && <BlickPlusOnboardingManagerWithErrorBoundary />}
    </>
  )
}

export default NonErrorPagePreCSSComponents
